<template>
  <div class="ChecklistTableTaskMenu">
    <v-menu>
      <template #activator="{props: menuProps}">
        <!-- We use a bare v-tooltip here because AppTooltip has trouble when combined with another activator -->
        <v-tooltip location="top" transition="fade-transition">
          <template #activator="{ props: tooltipProps }">
            <app-button
              size="small"
              type="icon"
              v-bind="{ ...menuProps, ...tooltipProps }"
              data-locator="other-actions-menu-button"
            >
              <app-icon-ds
                icon="ellipsis"
                size="xs"
              />
            </app-button>
          </template>
          <span>{{ $t('common.additionalActions') }}</span>
        </v-tooltip>
      </template>
      <v-list data-locator="checklist-task-menu">
        <v-list-item link data-locator="open-pane-menu-item" @click="onOpenChecklistPane('details')">
          <v-list-item-title>
            <app-icon-ds
              icon="sidebar-flip"
              size="xs"
              class="mr-2"
            />
            {{ t('openChecklistPane') }}
          </v-list-item-title>
        </v-list-item>
        <v-list-item link data-locator="attached-files-menu-item" @click="onOpenChecklistPane('files')">
          <v-list-item-title>
            <app-icon-ds
              icon="paperclip"
              size="xs"
              class="mr-2"
            />
            {{ t('attachedFiles') }}
          </v-list-item-title>
        </v-list-item>
        <v-list-item link data-locator="show-comments-menu-item" @click="onOpenChecklistPane('comments')">
          <v-list-item-title>
            <app-icon-ds
              icon="messages"
              size="xs"
              class="mr-2"
            />
            {{ t('comments') }}
          </v-list-item-title>
        </v-list-item>
        <template v-if="isCurrentUserPm">
          <v-list-item link data-locator="sharing-rights-menu-item" @click="onOpenChecklistPane('rights')">
            <v-list-item-title>
              <app-icon-ds
                icon="user-lock"
                size="xs"
                class="mr-2"
              />
              {{ t('share') }}
            </v-list-item-title>
          </v-list-item>
          <v-divider class="mx-2 my-1 text-grey-100" :opacity="1"/>
          <v-list-item link data-locator="duplicate-menu-item" @click="prepareDuplicateTask">
            <v-list-item-title>
              <app-icon-ds
                icon="copy"
                size="xs"
                class="mr-2"
              />
              {{ t('duplicate') }}
            </v-list-item-title>
          </v-list-item>
          <v-list-item link class="rename-button" data-locator="rename-menu-item" @click="$emit('edit-task-title')">
            <v-list-item-title class="rename-button">
              <app-icon-ds
                icon="pen"
                size="xs"
                class="mr-2"
              />
              <span class="rename-button">{{ t('rename') }}</span>
            </v-list-item-title>
          </v-list-item>
          <v-divider class="mx-2 my-1 text-grey-100" :opacity="1"/>
          <v-list-item v-if="hasResponsibles" link data-locator="contact-responsible-menu-item" @click="contactResponsibleDialogIsOpen = true">
            <v-list-item-title>
              <app-icon-ds
                icon="alarm-clock"
                size="xs"
                class="mr-2"
              />
              {{ t('contactResponsible') }}
            </v-list-item-title>
          </v-list-item>
          <AppDownloadFile :link="exportToPdfLink">
            <template #default="{submitForm}">
              <v-list-item link data-locator="export-comments-menu-item" @click="submitForm">
                <v-list-item-title>
                  <app-icon-ds
                    icon="arrow-up-from-line"
                    size="xs"
                    class="mr-2"
                  />
                  {{ t('exportComments') }}
                </v-list-item-title>
              </v-list-item>
            </template>
          </AppDownloadFile>
          <v-divider class="mx-2 my-1 text-grey-100" :opacity="1"/>
          <v-list-item link data-locator="delete-menu-item" @click="deleteTaskDialogIsOpen = true">
            <v-list-item-title class="text-error">
              <app-icon-ds
                icon="trash-can"
                size="xs"
                class="mr-2 text-error"
              />
              {{ $t('common.delete') }}
            </v-list-item-title>
          </v-list-item>
        </template>
      </v-list>
    </v-menu>

    <ChecklistDeleteTaskDialog
      v-if="deleteTaskDialogIsOpen"
      :mnemo="mnemo"
      :task="item"
      @close="deleteTaskDialogIsOpen = false"
      @onDelete="refreshChecklist"
    />

    <ContactResponsibleDialog
      v-if="contactResponsibleDialogIsOpen"
      :mnemo="mnemo"
      :tasks="[item]"
      @close="contactResponsibleDialogIsOpen = false"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'

import AppDownloadFile from '@/common/AppDownloadFile.vue'
import { toast } from '@/common/utils/toast'
import { useTranslation } from '@/common/utils/translation'
import ChecklistDeleteTaskDialog from '@/project/checklist/dialogs/ChecklistDeleteTaskDialog.vue'
import ContactResponsibleDialog from '@/project/checklist/dialogs/ContactResponsibleDialog.vue'
import { DUPLICATE_TASK, GET_CHECKLIST, OPEN_CHECKLIST_PANE } from '@/store/modules/checklist/action_types'

export default {
  name: 'ChecklistTableTaskMenu',
  components: {
    AppDownloadFile,
    ChecklistDeleteTaskDialog,
    ContactResponsibleDialog,
  },
  props: {
    mnemo: {
      type: String,
      required: true,
    },
    item: {
      type: Object,
      required: true,
    },
    parent: {
      type: Object,
      required: true,
    },
  },
  data () {
    return {
      attachedFilesDialogIsOpen: false,
      contactResponsibleDialogIsOpen: false,
      deleteTaskDialogIsOpen: false,
    }
  },
  setup () {
    return useTranslation({ keyPrefix: 'project.checklist.table.ChecklistTableTaskMenu.menu' })
  },
  computed: {
    ...mapGetters('room', ['isCurrentUserPm']),
    exportToPdfLink () {
      return `${import.meta.env.VUE_APP_API_URL}/room/${this.mnemo}/downloads/todoTask/${this.item.id}/comments-pdf`
    },
    hasResponsibles () {
      if (!this.item.rights?.responsibles) {
        return false
      }
      
      const hasGroups = this.item.rights.responsibles.groups && this.item.rights.responsibles.groups.length > 0
      const hasUsers = this.item.rights.responsibles.users && this.item.rights.responsibles.users.length > 0
      
      return hasGroups || hasUsers
    },
  },
  methods: {
    ...mapActions('checklist', [DUPLICATE_TASK, OPEN_CHECKLIST_PANE, GET_CHECKLIST]),
    onOpenChecklistPane(tab) {
      this.OPEN_CHECKLIST_PANE({
        isOpen: true,
        parent: this.parent,
        tab: tab,
        tasks: [this.item],
      })
    },
    async prepareDuplicateTask () {
      try {
        await this.DUPLICATE_TASK({
          mnemo: this.mnemo,
          taskId: this.item.id,
        })
        this.refreshChecklist()
      } catch (error) {
        console.error(error)
        toast.error(this.$t('project.checklist.table.ChecklistTableTaskMenu.duplicateTaskError'))
      }
    },
    refreshChecklist () {
      this.GET_CHECKLIST(this.mnemo)
    },
  },
}
</script>
<style scoped lang="scss">
.ChecklistTableTaskMenu {
  background-color: inherit;
  display: flex;
  justify-content: center;
  padding: 6px 12px;
  position: sticky;
  right: 0;

  .checklist-table-task:not(.drag-in-progress):not(.selected):not(:hover) & {
    background-color: white;
  }
}
</style>
